import React, { useEffect, useRef, useState } from 'react';
import { useIsNative } from '../utils/useIsNative';
import { isPlatform } from '../utils/isPlatform';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { Keyboard } from '@capacitor/keyboard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperProps } from 'swiper';
import { Mousewheel, Pagination } from 'swiper/modules';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserOnboardingState } from '../onboarding/UserOnboardingState';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AuthHandler } from '../auth/AuthHandler';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ISmartStorageProvider, LocalStorageProvider } from '../support/LocalStorageProvider';
import { PushNotificationService } from '../push-notifications/PushNotificationService';
import { UserState } from '../user/UserState';
import { PreAuthProfileService } from '../user/PreAuthProfileService';
import { TOS_CONSENT_VERSION, TOS_CONSENT_TEXT } from '../user/consentText';
import { FullScreenOverlayState } from '../support/FullScreenOverlayState';
import { ConversationsSlide, CONVERSATIONS_SLIDE_HEADING } from './ConversationsSlide';
import { NoteSummariesSlide, NOTE_SUMMARIES_SLIDE_HEADING } from './NoteSummariesSlide';
import { NotificationsSlide, NOTIFICATIONS_SLIDE_HEADING } from './NotificationsSlide';
import { CareTogetherSlide, CARE_TOGETHER_SLIDE_HEADING } from './CareTogetherSlide';
import { ProfileSlide, PROFILE_SLIDE_HEADING } from './ProfileSlide';
import { IDoneHandler, ISignIn } from './WelcomeTypes';

import 'swiper/css';
import 'swiper/css/pagination';

export class WelcomeStorageProvider {
    completed: ISmartStorageProvider<boolean>;

    static inject = () => [LocalStorageProvider];
    constructor(storage: LocalStorageProvider) {
        this.completed = storage.getBooleanProvider('welcome-completed', { userNeutral: true });
    }
}

export const Welcome: React.FC = () => {
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const isNative = useIsNative();

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);
    const authHandler = useInstance(AuthHandler);

    const onboarding = useInstance(UserOnboardingState);
    useSubscription(() => onboarding);

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const { completed } = useInstance(WelcomeStorageProvider);
    const preAuthProfile = useInstance(PreAuthProfileService);
    const overlayState = useInstance(FullScreenOverlayState);

    const [show, setShow] = useState(false);
    const tosConsentRecordedRef = useRef(false);

    const pushNotificationService = useInstance(PushNotificationService);
    const [notificationPermission, setNotificationPermission] = useState('disabled');

    useEffect(() => {
        if (isNative) {
            pushNotificationService.checkPermissions().then(setNotificationPermission);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // TODO add tests for welcome slides
        if (isAutomatedTests()) {
            return;
        }
        // if signed in after completing anonymously, transfer flag from storage to dynamo
        if (!isAnonymous && completed.get()) {
            onboarding.completeStep('recording').then(completed.remove);
            return;
        }
        // don't show when deep linking anywhere other than home page
        if (!['/', '/caregiver'].includes(history.getCurrentLocation().pathname)) {
            return;
        }
        // don't show if paywall is triggering
        if (history.getCurrentSearchParams().has('paywallTrigger')) {
            return;
        }
        // don't show if flag set in local storage for anonymous user
        if (isAnonymous && completed.get()) {
            return;
        }
        // don't show if flag set in dynamo for signed-in user
        if (!isAnonymous && (!onboarding.isLoaded() || onboarding.isStepComplete('recording'))) {
            return;
        }
        // made it through all the checks, so show
        setShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboarding.state?.loaded, onboarding.state?.steps.recording]);

    useEffect(() => {
        if (show) {
            mixpanel.track('Welcome Onboarding Opened');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        if (show) {
            overlayState.open('welcome');
            return () => overlayState.close('welcome');
        }
    }, [show, overlayState]);

    const [keyboardOpen, setKeyboardOpen] = useState(false);

    useEffect(() => {
        if (isPlatform('capacitor')) {
            const showListener = Keyboard.addListener('keyboardWillShow', () => {
                setKeyboardOpen(true);
            });
            const hideListener = Keyboard.addListener('keyboardDidHide', () => {
                setKeyboardOpen(false);
            });
            return () => {
                showListener.then(h => h.remove());
                hideListener.then(h => h.remove());
            };
        }
    }, []);

    // Capture showProfileSlide at mount time so it doesn't change when userState updates
    // (otherwise the slide will be removed before the slides have time to hide)
    const showProfileSlideRef = useRef(isAnonymous || !userState.hasCompleteProfile());

    if (!show) {
        return null;
    }

    const showProfileSlide = showProfileSlideRef.current;

    const onDone: IDoneHandler = async ({ hide, trackingFrom, fieldsEntered }) => {
        if (hide) {
            setShow(false);
        }
        if (isAnonymous) {
            // if anonymous, set flag in local storage
            completed.set(true);
            // notify subscribers that welcome is complete
            onboarding.notifyWelcomeComplete();
        } else {
            // if signed in, set flag in dynamo
            await onboarding.completeStep('recording');
        }
        mixpanel.track('Welcome Flow Completed', () => ({
            By: trackingFrom,
            ...(fieldsEntered ? { 'Fields Entered': fieldsEntered } : {})
        }));
    };

    const signIn: ISignIn = async ({ trackingFrom, returnTo }) => {
        await authHandler.login({ trackingFrom, returnTo });
    };

    const onTermsClick = () => {
        mixpanel.track('Terms of Service Opened');
    };

    const onConversationsSlideAdvance = async () => {
        const hasTosConsent = tosConsentRecordedRef.current
            || (userState.state.consents?.some(c => c.consentType === 'terms-of-service') ?? false);
        if (hasTosConsent) {
            return;
        }
        tosConsentRecordedRef.current = true;

        const tosConsent = {
            consentType: 'terms-of-service',
            consentedAt: new Date().toISOString(),
            disclosureVersion: TOS_CONSENT_VERSION,
            disclosureText: TOS_CONSENT_TEXT
        };
        if (isAnonymous) {
            await preAuthProfile.storeConsent(tosConsent);
        } else {
            userState.addConsent(tosConsent);
        }
    };

    const onRequestNotificationPermission = async () => {
        mixpanel.track('Notification Dialog Viewed', () => ({ Location: 'welcome', Response: 'Yes' }));
        setNotificationPermission(await pushNotificationService.requestPermission());
    };

    const onSkipNotifications = () => {
        mixpanel.track('Notification Dialog Viewed', () => ({ Location: 'welcome', Response: 'Maybe later' }));
    };

    // Build the slide headings list for tracking (must match rendered slide order)
    const slideHeadings = [
        CONVERSATIONS_SLIDE_HEADING,
        NOTE_SUMMARIES_SLIDE_HEADING,
        ...(isNative ? [NOTIFICATIONS_SLIDE_HEADING] : []),
        CARE_TOGETHER_SLIDE_HEADING,
        ...(showProfileSlide ? [PROFILE_SLIDE_HEADING] : [])
    ];

    // The notifications slide index (only exists on native)
    const notificationsSlideIndex = isNative ? 2 : -1;

    // when android is rotated to landscape, the navbar doesn't move, so avoid an unnecessary white bar in this scenario
    const paddingBottom = (isPlatform('android') ? undefined : 'var(--ion-safe-area-bottom)');

    let handlingAction = false;

    return (
        <div className={keyboardOpen ? 'keyboard-open' : undefined} style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', paddingBottom, backgroundColor: 'white', overflow: 'hidden', zIndex: 1}}>
            <Swiper
                style={{height: '100%', width: '100%'}}
                modules={[Pagination, Mousewheel]}
                slidesPerView={1}
                centeredSlides
                mousewheel
                pagination={{ clickable: true }}

                // on swipe next: if swiping past notifications slide, trigger permission request
                onBeforeTransitionStart={async (swiper: SwiperProps) => {
                    if (swiper.swipeDirection === 'next' && !handlingAction && swiper.activeIndex === notificationsSlideIndex + 1 && notificationsSlideIndex >= 0) {
                        const canRequest = !['granted', 'denied', 'disabled'].includes(notificationPermission);
                        if (canRequest) {
                            try {
                                handlingAction = true;
                                swiper.slidePrev(0, false);
                                await onRequestNotificationPermission();
                                swiper.slideNext(0, false);
                            } finally {
                                handlingAction = false;
                            }
                        }
                    }
                }}

                onSlideChange={s => {
                    const event = (s.previousIndex < s.activeIndex ? 'Welcome Screen Advanced' : 'Welcome Screen Reversed');
                    mixpanel.track(event, () => ({
                        From: `${s.previousIndex + 1}: ${slideHeadings[s.previousIndex]}`,
                        To: `${s.activeIndex + 1}: ${slideHeadings[s.activeIndex]}`
                    }));

                    // track when the profile slide is viewed
                    if (showProfileSlide && s.activeIndex === slideHeadings.length - 1) {
                        mixpanel.track('Profile Collection Viewed', () => ({ Location: 'welcome' }));
                    }
                }}
            >
                <SwiperSlide key="slide-conversations" style={{width: '100%', height: '100%'}}>
                    <ConversationsSlide isAnonymous={isAnonymous} signIn={signIn} onTermsClick={onTermsClick} onAdvance={onConversationsSlideAdvance} />
                </SwiperSlide>

                <SwiperSlide key="slide-notes" style={{width: '100%', height: '100%'}}>
                    <NoteSummariesSlide />
                </SwiperSlide>

                {isNative && (
                    <SwiperSlide key="slide-notifications" style={{width: '100%', height: '100%'}}>
                        <NotificationsSlide
                            notificationPermission={notificationPermission}
                            onRequestPermission={onRequestNotificationPermission}
                            onSkip={onSkipNotifications}
                        />
                    </SwiperSlide>
                )}

                <SwiperSlide key="slide-care" style={{width: '100%', height: '100%'}}>
                    <CareTogetherSlide isLastSlide={!showProfileSlide} onDone={onDone} />
                </SwiperSlide>

                {showProfileSlide && (
                    <SwiperSlide key="slide-profile" style={{width: '100%', height: '100%'}}>
                        <ProfileSlide
                            onDone={onDone}
                            signIn={isAnonymous ? signIn : undefined}
                            isAnonymous={isAnonymous}
                            keyboardOpen={keyboardOpen}
                        />
                    </SwiperSlide>
                )}
            </Swiper>
        </div>
    );
};
